import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Chip, Button, IconButton, Tooltip, Link} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import {ROLES} from '../../config/roles';
import Protected from '../../components/Protected';

const XpLevelConfigsPage = () => {
    const navigate = useNavigate();
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchConfigs = useCallback(() => {
        setLoading(true);
        apiService.get('/api/xp-level-configs')
            .then(data => setConfigs(data))
            .catch(err => setError(err.message || 'Failed to fetch XP level configs'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchConfigs();
    }, [fetchConfigs]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/xp-level-configs/${itemToDelete.levelNumber}`);
            fetchConfigs();
        } catch (err) {
            setError('Failed to delete level config.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'levelNumber',
            header: 'Level',
            size: 80,
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/xp-level-configs/${cell.getValue()}`}>
                    {cell.getValue()}
                </Link>
            ),
        },
        {
            accessorKey: 'xpThreshold',
            header: 'XP Threshold',
            size: 120,
            Cell: ({cell}) => cell.getValue()?.toLocaleString() ?? '0',
        },
        {
            id: 'hasReward',
            header: 'Reward',
            size: 100,
            Cell: ({row}) => (
                <Chip
                    label={row.original.reward?.length ? 'Yes' : 'None'}
                    color={row.original.reward?.length ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            accessorKey: 'createdAt',
            header: 'Created',
            size: 150,
            Cell: ({cell}) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '-',
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: configs,
        enablePagination: false,
        enableBottomToolbar: false,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {
            sorting: [{id: 'levelNumber', desc: false}],
        },
        renderTopToolbarCustomActions: () => (
            <Protected component={XpLevelConfigsPage} action="create">
                <Button variant="contained" startIcon={<AddIcon/>}
                        onClick={() => navigate('/xp-level-configs/new')}>
                    Add Level
                </Button>
            </Protected>
        ),
        enableRowActions: true,
        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions', size: 100}},
        positionActionsColumn: 'last',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    <IconButton component={RouterLink}
                                to={`/xp-level-configs/${row.original.levelNumber}`}>
                        <EditIcon/>
                    </IconButton>
                </Tooltip>
                <Protected component={XpLevelConfigsPage} action="delete">
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => {
                            setItemToDelete(row.original);
                            setConfirmOpen(true);
                        }}>
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>XP Level Config</Typography>
            <MaterialReactTable table={table}/>
            <Protected component={XpLevelConfigsPage} action="delete">
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={handleDeleteItem}
                    title={`Delete Level ${itemToDelete?.levelNumber}?`}
                    message="Are you sure you want to delete this level config? This action cannot be undone."
                />
            </Protected>
        </Box>
    );
};

XpLevelConfigsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default XpLevelConfigsPage;
